import { devDebug } from "../lib/devLog";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Newspaper, Trash2, ExternalLink, Upload, Clock } from "lucide-react";
import {
  fetchDailyNews,
  fetchUpcomingDailyNews,
  publishDailyNews,
  removeDailyNews,
  removeDailyNewsById,
  uploadDailyNewsMedia,
  hasModeratorToken,
  toEmbedUrl,
} from "../lib/dailyNewsApi";

const API = process.env.REACT_APP_BACKEND_URL || "";

// The banner sits between the FeedTabs and the first VoicePost. Its vertical
// height matches the in-feed ad (image area 130px + 1.5 py caption strip),
// its width fills the same container padding used by the ComposerBar, so it
// visually aligns with the transmission bar at the bottom.

function absoluteMediaUrl(mediaUrl) {
  // Stored URLs that start with `/api/v1/uploads/` are gateway-relative;
  // prepend REACT_APP_BACKEND_URL so <img>/<video> resolve them cross-origin.
  if (mediaUrl?.startsWith("/api/v1/uploads/")) return `${API}${mediaUrl}`;
  return mediaUrl;
}

// Formats the delta between now and `expiresAt` as "in Xh Ym" / "in Xm" /
// "scaduta" so viewers see how fresh the news still is. Auto-tick every 60s.
function useTimeLeft(expiresAtIso, startsAtIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!expiresAtIso) return undefined;
    const id = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [expiresAtIso]);
  return useMemo(() => {
    if (!expiresAtIso) return null;
    const expiresAtMs = new Date(expiresAtIso).getTime();
    const startsAtMs = startsAtIso ? new Date(startsAtIso).getTime() : expiresAtMs - 24 * 60 * 60 * 1000;
    const diffMs = expiresAtMs - now;
    if (diffMs <= 0) return { expired: true, label: "Scaduta", progress: 100 };
    const totalMin = Math.floor(diffMs / 60_000);
    const hours = Math.floor(totalMin / 60);
    const mins = totalMin % 60;
    // Progress fraction of the total slot that has already elapsed. Clamped
    // to [0, 100] so the bar renders sensibly if `startsAtIso` is missing.
    const totalWindowMs = Math.max(1, expiresAtMs - startsAtMs);
    const elapsedMs = Math.min(totalWindowMs, Math.max(0, now - startsAtMs));
    const progress = Math.round((elapsedMs / totalWindowMs) * 100);
    if (hours >= 1) return { expired: false, label: `Termina fra ${hours}h ${String(mins).padStart(2, "0")}m`, progress };
    return { expired: false, label: `Termina fra ${Math.max(1, mins)}m`, progress };
  }, [expiresAtIso, startsAtIso, now]);
}

function BannerMedia({ news }) {
  const resolvedUrl = absoluteMediaUrl(news.mediaUrl);
  if (news.mediaType === "image") {
    return (
      <img
        src={resolvedUrl}
        alt={news.caption}
        className="w-full h-[130px] object-cover"
        data-testid="daily-news-image"
      />
    );
  }
  if (news.mediaType === "video") {
    return (
      <video
        src={resolvedUrl}
        controls
        playsInline
        preload="metadata"
        className="w-full h-[130px] object-cover bg-black"
        data-testid="daily-news-video"
      />
    );
  }
  // embed (YouTube / Vimeo). SEC-002 fix (iter31): sandbox restricts the
  // iframe to only what the video players need. `allow-scripts` lets the
  // player run, `allow-same-origin` keeps the player's cookies working, and
  // `allow-presentation` enables full-screen. We intentionally omit
  // `allow-top-navigation` so a hostile embed cannot redirect the parent.
  return (
    <iframe
      src={resolvedUrl}
      title={news.caption}
      className="w-full h-[130px]"
      style={{ border: 0 }}
      sandbox="allow-scripts allow-same-origin allow-presentation"
      referrerPolicy="strict-origin-when-cross-origin"
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
      data-testid="daily-news-embed"
    />
  );
}

// Extracted from JSX to replace a 5-level nested ternary. Encodes the label
// state machine for the AdminForm submit button.
function submitLabel({ submitting, scheduleOpen, current }) {
  if (submitting) return scheduleOpen ? "Programmazione…" : "Pubblicazione…";
  if (scheduleOpen) return "Programma banner";
  return current ? "Sostituisci banner" : "Pubblica banner";
}

function AdminForm({ topicSlug, current, onDone, onCancel }) {
  const [mediaType, setMediaType] = useState(current?.mediaType || "image");
  const [mediaUrl, setMediaUrl] = useState(current?.mediaUrl || "");
  const [caption, setCaption] = useState(current?.caption || "Notizia del giorno");
  const [linkUrl, setLinkUrl] = useState(current?.linkUrl || "");
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [startsAtLocal, setStartsAtLocal] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      const uploaded = await uploadDailyNewsMedia(file);
      setMediaUrl(uploaded.url);
      setMediaType(uploaded.kind === "video" ? "video" : "image");
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Upload fallito");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const handleUrlChange = (val) => {
    setMediaUrl(val);
    // Auto-detect YouTube/Vimeo — the caller can override the media type manually.
    const embed = toEmbedUrl(val);
    if (embed) {
      setMediaType("embed");
      setMediaUrl(embed);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // Convert the datetime-local value (browser local time) into ISO with
      // timezone before sending, so the server sees an unambiguous instant.
      const startsAtIso = scheduleOpen && startsAtLocal
        ? new Date(startsAtLocal).toISOString()
        : null;
      await publishDailyNews(topicSlug, {
        mediaType,
        mediaUrl: mediaUrl.trim(),
        caption: caption.trim() || "Notizia del giorno",
        linkUrl: linkUrl.trim() || null,
        startsAt: startsAtIso,
      });
      onDone();
    } catch (err) {
      setError(err?.response?.data?.message || err?.response?.data?.detail || err?.message || "Errore");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="my-4 card p-4 space-y-3"
      data-testid="daily-news-admin-form"
    >
      <div className="flex items-center gap-2">
        <Newspaper size={16} className="text-primary" />
        <div className="font-semibold text-sm">Pannello moderatore · Notizia del giorno</div>
        <button
          type="button"
          onClick={onCancel}
          className="ml-auto text-xs text-muted hover:text-ink"
          data-testid="daily-news-admin-cancel"
        >
          Chiudi
        </button>
      </div>

      <div className="flex flex-wrap gap-2 text-xs" role="tablist">
        {[
          { k: "image", l: "Immagine" },
          { k: "video", l: "Video" },
          { k: "embed", l: "YouTube / Vimeo" },
        ].map((t) => (
          <button
            key={t.k}
            type="button"
            onClick={() => setMediaType(t.k)}
            className={`rounded-full px-3 py-1 border ${
              mediaType === t.k
                ? "bg-ink text-white border-ink"
                : "bg-white text-ink border-edge hover:border-ink"
            }`}
            data-testid={`daily-news-type-${t.k}`}
          >
            {t.l}
          </button>
        ))}
      </div>

      <div className="flex items-stretch gap-2">
        <input
          type="url"
          value={mediaUrl}
          onChange={(e) => handleUrlChange(e.target.value)}
          placeholder={
            mediaType === "embed"
              ? "https://youtu.be/... oppure https://vimeo.com/..."
              : "URL immagine/video (o carica un file →)"
          }
          className="flex-1 rounded-lg border border-edge px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
          required
          data-testid="daily-news-url-input"
        />
        {mediaType !== "embed" && (
          <label className="inline-flex items-center gap-1 rounded-lg border border-edge px-3 py-2 text-xs cursor-pointer hover:border-ink whitespace-nowrap">
            {uploading ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            {uploading ? "Carico…" : "Carica file"}
            <input
              ref={fileRef}
              type="file"
              accept={mediaType === "video" ? "video/*" : "image/*"}
              className="hidden"
              onChange={handleFile}
              data-testid="daily-news-file-input"
            />
          </label>
        )}
      </div>

      <input
        type="text"
        value={caption}
        onChange={(e) => setCaption(e.target.value)}
        maxLength={240}
        placeholder="Notizia del giorno"
        className="w-full rounded-lg border border-edge px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
        data-testid="daily-news-caption-input"
      />

      <input
        type="url"
        value={linkUrl}
        onChange={(e) => setLinkUrl(e.target.value)}
        placeholder="Link opzionale (verrà mostrato come pulsante)"
        className="w-full rounded-lg border border-edge px-3 py-2 text-xs focus:outline-none focus:ring-2 focus:ring-primary/40"
        data-testid="daily-news-link-input"
      />

      <div className="pt-1">
        <label className="inline-flex items-center gap-2 text-xs text-muted cursor-pointer select-none">
          <input
            type="checkbox"
            checked={scheduleOpen}
            onChange={(e) => setScheduleOpen(e.target.checked)}
            className="w-3.5 h-3.5"
            data-testid="daily-news-schedule-toggle"
          />
          Programma per una data/ora futura
        </label>
        {scheduleOpen && (
          <div className="mt-2 flex items-center gap-2">
            <input
              type="datetime-local"
              value={startsAtLocal}
              onChange={(e) => setStartsAtLocal(e.target.value)}
              className="rounded-lg border border-edge px-3 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-primary/40"
              data-testid="daily-news-schedule-input"
              min={new Date(Date.now() + 60_000).toISOString().slice(0, 16)}
            />
            <span className="text-[11px] text-muted">
              La notizia scatterà a quell'orario e durerà 24h. Max 30 giorni nel futuro.
            </span>
          </div>
        )}
      </div>

      {error && (
        <div className="text-xs text-primary" data-testid="daily-news-admin-error">{error}</div>
      )}

      <div className="flex items-center gap-2 pt-1">
        <button
          type="submit"
          disabled={submitting || uploading || !mediaUrl.trim()}
          className="btn-primary text-sm px-4 py-2 disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="daily-news-admin-submit"
        >
          {submitLabel({ submitting, scheduleOpen, current })}
        </button>
        <div className="text-[11px] text-muted">Scade automaticamente 24h dopo la partenza.</div>
      </div>
    </form>
  );
}

function AdminBar({ topicSlug, news, onChanged, onOpenForm }) {
  const [removing, setRemoving] = useState(false);
  const doRemove = async () => {
    if (!window.confirm("Rimuovere la Notizia del giorno da questo argomento?")) return;
    setRemoving(true);
    try {
      await removeDailyNews(topicSlug);
      onChanged();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.message || err?.message || "Errore");
    } finally {
      setRemoving(false);
    }
  };
  return (
    <div
      className="flex items-center gap-2 mt-1 text-[11px] text-muted"
      data-testid="daily-news-admin-bar"
    >
      <Newspaper size={12} className="text-primary" />
      <span>Pannello moderatore</span>
      <button
        type="button"
        onClick={onOpenForm}
        className="ml-auto rounded-full border border-edge px-2 py-0.5 hover:border-ink hover:text-ink"
        data-testid="daily-news-admin-open"
      >
        {news ? "Sostituisci" : "Pubblica"}
      </button>
      {news && (
        <button
          type="button"
          onClick={doRemove}
          disabled={removing}
          className="rounded-full border border-primary/40 text-primary px-2 py-0.5 hover:bg-primary hover:text-white flex items-center gap-1 disabled:opacity-40"
          data-testid="daily-news-admin-remove"
        >
          <Trash2 size={11} /> {removing ? "Rimuovo…" : "Rimuovi"}
        </button>
      )}
    </div>
  );
}

function UpcomingList({ topicSlug, refreshKey, onRemoved }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchUpcomingDailyNews(topicSlug)
      .then((res) => {
        if (!cancelled) setItems(res);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [topicSlug, refreshKey]);

  if (loading && items.length === 0) return null;
  if (items.length === 0) return null;

  const doRemove = async (id) => {
    try {
      await removeDailyNewsById(topicSlug, id);
      setItems((prev) => prev.filter((i) => i.id !== id));
      onRemoved?.();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.message || err?.message || "Errore");
    }
  };

  return (
    <div className="mt-3 card p-3" data-testid="daily-news-upcoming">
      <div className="flex items-center gap-2 mb-2">
        <Clock size={13} className="text-primary" />
        <div className="text-xs font-semibold uppercase tracking-widest">
          Notizie programmate · {items.length}
        </div>
      </div>
      <div className="space-y-1.5">
        {items.map((n) => (
          <div
            key={n.id}
            className="flex items-center gap-2 text-xs bg-cream/60 rounded-lg px-2 py-1.5"
            data-testid={`daily-news-upcoming-${n.id}`}
          >
            <span className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
            <span className="font-medium truncate flex-1">{n.caption}</span>
            <span className="text-muted tabular-nums shrink-0">
              {new Date(n.startsAt).toLocaleString("it-IT", {
                day: "2-digit",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
            <button
              type="button"
              onClick={() => doRemove(n.id)}
              className="text-primary hover:text-white hover:bg-primary rounded-full w-6 h-6 flex items-center justify-center border border-primary/30"
              aria-label={`Rimuovi notizia programmata "${n.caption}"`}
              data-testid={`daily-news-upcoming-remove-${n.id}`}
            >
              <Trash2 size={11} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export function DailyNewsBanner({ topicSlug, adminMode = false }) {
  const [news, setNews] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(adminMode);
  const modAvailable = hasModeratorToken();
  const [upcomingRefresh, setUpcomingRefresh] = useState(0);
  const timeLeft = useTimeLeft(news?.expiresAt, news?.startsAt);

  const load = React.useCallback(async () => {
    if (!topicSlug) return;
    try {
      const n = await fetchDailyNews(topicSlug);
      setNews(n);
    } catch (err) {
      devDebug("DailyNewsBanner: fetch failed", err?.message);
      setNews(null);
    } finally {
      setLoading(false);
    }
  }, [topicSlug]);

  const refreshAll = React.useCallback(() => {
    load();
    setUpcomingRefresh((v) => v + 1);
  }, [load]);

  useEffect(() => {
    setLoading(true);
    setFormOpen(adminMode);
    load();
  }, [adminMode, load]);

  useEffect(() => {
    if (!topicSlug || typeof window === "undefined") return undefined;

    const refresh = () => {
      void load();
    };
    const onChanged = (event) => {
      if (!event.detail?.topicSlug || event.detail.topicSlug === topicSlug) refresh();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };

    const intervalId = window.setInterval(refresh, 5000);
    window.addEventListener("focus", refresh);
    window.addEventListener("ol:daily-news-changed", onChanged);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("ol:daily-news-changed", onChanged);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [load, topicSlug]);

  const embedResolved = useMemo(() => {
    if (!news || news.mediaType !== "embed") return null;
    return toEmbedUrl(news.mediaUrl) || news.mediaUrl;
  }, [news]);

  // Precompute the news object with the resolved embed URL so we don't
  // rebuild it inline on every render (breaks memoization of BannerMedia).
  const newsWithResolvedMedia = useMemo(() => {
    if (!news) return null;
    if (news.mediaType !== "embed") return news;
    return { ...news, mediaUrl: embedResolved || news.mediaUrl };
  }, [news, embedResolved]);

  // Everything below is inside the `px-6` container of the topic card so it
  // aligns with the ComposerBar horizontally (same max-w-[1100px] wrapper).

  if (loading) {
    return (
      <div
        className="my-4 h-[130px] rounded-2xl border border-edge bg-cream/60 animate-pulse"
        data-testid="daily-news-loading"
      />
    );
  }

  // Nothing to show and viewer is not admin → render null (user experience
  // stays 100% unchanged when the moderator has not published anything).
  if (!news && !(adminMode && modAvailable)) return null;

  return (
    <div className="my-4" data-testid="daily-news-banner">
      {news && (
        <div className="card overflow-hidden relative">
          <div className="relative">
            <BannerMedia news={newsWithResolvedMedia} />
            <span className="absolute top-2 left-2 bg-primary text-white text-[10px] uppercase tracking-widest rounded-full px-2 py-0.5 font-semibold">
              Notizia del giorno
            </span>
            {news.linkUrl && (
              <a
                href={news.linkUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="absolute bottom-2 right-2 inline-flex items-center gap-1 bg-white/95 hover:bg-white text-ink text-[11px] rounded-full px-2.5 py-1 font-medium shadow"
                data-testid="daily-news-cta"
              >
                Approfondisci <ExternalLink size={11} />
              </a>
            )}
          </div>
          <div
            className="flex items-center justify-between gap-2 text-[12px] text-ink font-medium py-1.5 bg-cream px-4"
            data-testid="daily-news-caption-row"
          >
            <span
              className="truncate flex-1 text-center"
              data-testid="daily-news-caption"
            >
              {news.caption}
            </span>
            {timeLeft && (
              <span
                className={`inline-flex items-center gap-1 shrink-0 text-[10px] font-semibold uppercase tracking-widest ${
                  timeLeft.expired ? "text-muted" : "text-primary"
                }`}
                data-testid="daily-news-countdown"
                title={`Scade: ${new Date(news.expiresAt).toLocaleString("it-IT")}`}
              >
                <Clock size={11} /> {timeLeft.label}
              </span>
            )}
          </div>
          {timeLeft && !timeLeft.expired && (
            // Slim progress bar showing how much of the 24h window has
            // elapsed. Grows from 0% (fresh) to 100% (expiring) — reinforces
            // urgency visually without adding noise.
            <div
              className="h-1 bg-cream overflow-hidden"
              data-testid="daily-news-progress"
              aria-hidden="true"
            >
              <div
                className="h-full bg-primary transition-all duration-500 ease-out"
                style={{ width: `${timeLeft.progress}%` }}
              />
            </div>
          )}
        </div>
      )}

      {adminMode && modAvailable && (
        <>
          {formOpen ? (
            <AdminForm
              topicSlug={topicSlug}
              current={news}
              onDone={() => {
                setFormOpen(false);
                refreshAll();
              }}
              onCancel={() => setFormOpen(false)}
            />
          ) : (
            <AdminBar
              topicSlug={topicSlug}
              news={news}
              onChanged={refreshAll}
              onOpenForm={() => setFormOpen(true)}
            />
          )}
          <UpcomingList
            topicSlug={topicSlug}
            refreshKey={upcomingRefresh}
            onRemoved={refreshAll}
          />
        </>
      )}
    </div>
  );
}
